import { useMemo, useState } from "react";
import { CheckCircle2, ShieldCheck } from "lucide-react";

import { CopyableUrl } from "@/components/CopyableUrl";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { useToast } from "@/components/ui/Toast";
import {
  useAckIncident,
  useIncidents,
  useResolveIncident,
} from "@/hooks/useIncidents";
import { useSites } from "@/hooks/useSites";
import type {
  IncidentsListParams,
  IncidentSeverity,
  IncidentStatusFilter,
} from "@/lib/api";

const STATUS_OPTIONS: { value: IncidentStatusFilter; label: string }[] = [
  { value: "open", label: "Активні" },
  { value: "resolved", label: "Розв'язані" },
  { value: "all", label: "Усі" },
];

function severityVariant(
  s: IncidentSeverity,
): "default" | "secondary" | "destructive" | "warning" {
  if (s === "critical") return "destructive";
  if (s === "warning") return "warning";
  return "secondary";
}

function formatDuration(opened: string, resolved: string | null): string {
  const start = new Date(opened).getTime();
  const end = resolved ? new Date(resolved).getTime() : Date.now();
  let ms = Math.max(0, end - start);
  const days = Math.floor(ms / 86_400_000);
  ms -= days * 86_400_000;
  const hours = Math.floor(ms / 3_600_000);
  ms -= hours * 3_600_000;
  const minutes = Math.floor(ms / 60_000);
  if (days > 0) return `${days}д ${hours}г`;
  if (hours > 0) return `${hours}г ${minutes}хв`;
  return `${minutes}хв`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("uk-UA");
  } catch {
    return iso;
  }
}

export default function IncidentsPage(): JSX.Element {
  const { toast } = useToast();
  const [status, setStatus] = useState<IncidentStatusFilter>("open");
  const [siteId, setSiteId] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  const sitesQ = useSites();
  const params = useMemo<IncidentsListParams>(() => {
    const p: IncidentsListParams = { status };
    if (siteId) p.site_id = Number(siteId);
    if (from) p.from = new Date(from).toISOString();
    if (to) p.to = new Date(to).toISOString();
    return p;
  }, [status, siteId, from, to]);

  const incidentsQ = useIncidents(params);
  const ackMut = useAckIncident();
  const resolveMut = useResolveIncident();

  async function onAck(id: number): Promise<void> {
    try {
      await ackMut.mutateAsync(id);
      toast({ title: "Підтверджено", variant: "success" });
    } catch {
      toast({ title: "Не вдалося підтвердити", variant: "destructive" });
    }
  }

  async function onResolve(id: number): Promise<void> {
    try {
      await resolveMut.mutateAsync(id);
      toast({ title: "Розв'язано", variant: "success" });
    } catch {
      toast({ title: "Не вдалося розв'язати", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Інциденти</h1>
        <p className="text-sm text-muted-foreground">
          Журнал відкритих та закритих інцидентів.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <Label>Статус</Label>
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as IncidentStatusFilter)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Сайт</Label>
          <Select value={siteId || "all"} onValueChange={(v) => setSiteId(v === "all" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="Усі" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі</SelectItem>
              {sitesQ.data?.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="inc-from">Від</Label>
          <Input
            id="inc-from"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inc-to">До</Label>
          <Input
            id="inc-to"
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Відкрито</TableHead>
              <TableHead>Сайт</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Тривалість</TableHead>
              <TableHead>Рівень</TableHead>
              <TableHead>Помилка</TableHead>
              <TableHead className="text-right">Дії</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {incidentsQ.isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                  Завантаження…
                </TableCell>
              </TableRow>
            ) : !incidentsQ.data || incidentsQ.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                  Інциденти за вибраними фільтрами відсутні.
                </TableCell>
              </TableRow>
            ) : (
              incidentsQ.data.map((inc) => (
                <TableRow key={inc.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(inc.opened_at)}
                  </TableCell>
                  <TableCell>
                    {inc.site_name ?? `#${inc.site_id ?? "?"}`}
                  </TableCell>
                  <TableCell className="max-w-[260px]">
                    <CopyableUrl
                      url={inc.url}
                      fallback={`URL #${inc.url_entry_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDuration(inc.opened_at, inc.resolved_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={severityVariant(inc.severity)}>
                      {inc.severity}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[260px] truncate text-xs text-destructive">
                    {inc.last_error ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {inc.resolved_at ? (
                      <Badge variant="secondary">closed</Badge>
                    ) : (
                      <div className="flex justify-end gap-1">
                        {!inc.acknowledged_at ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onAck(inc.id)}
                            disabled={ackMut.isPending}
                          >
                            <ShieldCheck className="mr-1 h-4 w-4" />
                            Ack
                          </Button>
                        ) : (
                          <Badge variant="secondary">ack</Badge>
                        )}
                        <Button
                          size="sm"
                          onClick={() => onResolve(inc.id)}
                          disabled={resolveMut.isPending}
                        >
                          <CheckCircle2 className="mr-1 h-4 w-4" />
                          Resolve
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
